import { LocalAuthHandler } from '@/utils/LocalAuthHandler';
import {
  clearScopedLinkContext,
  isScopedMerchantLinkSession,
} from '@/utils/scopedLinkSession';
import { syncPiniaApiPathPrefix } from '@/utils/syncPiniaApiPathPrefix';
import { useTokenStore } from '@authvia-v3/pinia-stores-root';
import { RemovableRef, useLocalStorage, useSessionStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { RouteLocationNormalizedLoaded } from 'vue-router';
import { LoggerService } from '@/utils/LoggerService';
import { SentryService } from '@/services';

const logger = new LoggerService('AppStore');

/**
 * Deals with top level application logic
 */
export const useAppStore = defineStore('application-store', {
  state: () => ({
    localToken: useLocalStorage<string | undefined>(
      'AUTHVIA_LOCK_TOKEN',
      undefined,
    ),
    sessionToken: useSessionStorage<string | undefined>(
      'AUTHVIA_LOCK_TOKEN',
      undefined,
    ),
    initPath: '',
  }),
  getters:{
    isAuthenticated: (state)=> state.localToken || state.sessionToken || LocalAuthHandler.getToken()
  },
  actions: {
    /**
     * Initialize tokens
     */
    initializeApp(query: RouteLocationNormalizedLoaded) {
      logger.log('initializeApp()', query);
      const tokenStore = useTokenStore();
      const token: string | undefined = LocalAuthHandler.getToken();

      if (token && !tokenStore.isExpired(token)) {
        if (isScopedMerchantLinkSession(token)) {
          this.localToken = undefined;
          this.sessionToken = token;
        } else {
          this.sessionToken = undefined;
          this.localToken = token;
        }
        tokenStore.setActiveToken(token);
        syncPiniaApiPathPrefix(tokenStore.parse, token);
      } else {
        this.clearToken();
      }
    },
    setToken(token: string) {
      if (isScopedMerchantLinkSession(token)) {
        this.localToken = undefined;
        this.sessionToken = token;
        return;
      }

      // Full OTP/wallet tokens must not keep SCOPED_* session keys — those keys
      // make the router bounce Home/list navigations back to the BP page.
      clearScopedLinkContext();
      this.sessionToken = undefined;
      this.localToken = token;
    },
    clearToken(): void {
      this.localToken = undefined;
      this.sessionToken = undefined;
      LocalAuthHandler.clearToken();
      syncPiniaApiPathPrefix(() => ({}), undefined);

      // Clear user information from Sentry
      void SentryService.clearUser();
    },
    logout(): void {
      this.clearToken();
    },
    cacheInBrowser<T>(
      key: string,
      data: T,
      useSession?: boolean,
    ): RemovableRef<T> {
      if (useSession) {
        return useSessionStorage<T>(key, data);
      } else {
        return useLocalStorage<T>(key, data);
      }
    },
    /**
     * AuthCode Login
     * @param token
     * @returns
     */
    isTemporaryAuthentication(token: string): boolean {
      const tokenStore = useTokenStore();
      const payload = tokenStore.parse(token);
      const { 'https://authvia.com/businessProcess': businessProcess } =
        payload;
      // If the token contains business-process id that means the user was authenticated for a single business-process
      if (businessProcess && businessProcess.trim().length > 5) {
        return false;
      }
      return true;
    },
    /**
     * Secure-Link Login
     * @param token
     * @returns
     */
    isLongerAuthentication(token: string): boolean {
      return !this.isTemporaryAuthentication(token);
    },

    /**
     * Save a variable in the local storage so that it is coming from trigger
     */
    setBusinessProcessRedirectPath() {
      localStorage.setItem('hasTrigger', 'true');
    },

    /**
     * Get trigger information
     * @returns
     */
    getBusinessProcessInitialPath() {
      return localStorage.getItem('hasTrigger') || undefined;
    },

    /* Remove trigger information
     * @returns
     */
    removeBusinessProcessInitialPath() {
      localStorage.removeItem('hasTrigger');
    },
  },
});
